import { useDashboard } from '@cobliteam/dashboard/src/providers/DashboardProvider';
import { DatadogTrack } from '@cobliteam/datadog-rum';
import useGlobalFilterStore from '@cobliteam/hooks/src/useGlobalFilterStore';
import { useSearchData } from '@cobliteam/hooks/src/useSearchStore';
import { useLanguage, useTranslation } from '@cobliteam/i18n';
import { Button } from '@cobliteam/ui/components';
import { InputSearch } from '@cobliteam/ui/form';
import Input from '@cobliteam/ui/form/Input';
import Icons from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import { getTextForLanguage } from '@cobliteam/ui-business/utils';
import { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useLocation } from 'react-router-dom';
import { SearchIcon } from './components/SearchIcon';
import GlobalSearchContent from './containers/GlobalSearchContent';
import { useGlobalSidebarDef } from './globalSidebarDef';
import { trackGlobalSearchEvents } from './helpers';
import { GlobalSearchProvider } from './provider/GlobalSearchProvider';
import { ProviderPersistQueryClient } from './provider/PersisterProvider';
import { type Category, type DataWithCategory, EnumCategory, type PageWithCategory } from './types';

const INPUT_SEARCH_CLASS = 'w-[300px]';

const GlobalSearchLoading = () => {
  return <InputSearch onChange={() => ''} loading size='sm' className={cn(INPUT_SEARCH_CLASS)} placeholder='Carregando a busca global...' disabled />;
};

const isMac = /macintosh|mac os x/i.test(navigator.userAgent);

const GlobalSearchIdleInput = ({ onOpen }: { onOpen: () => void }) => {
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        trackGlobalSearchEvents.openSearch();
        onOpen();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onOpen]);

  return (
    <div className='relative'>
      <div data-visual-test='blackout' className='relative flex items-center gap-2 rounded-lg border border-gray-6'>
        <Input
          data-testid='global-search-input'
          className='h-[34px] w-[426px] justify-between border-none bg-transparent'
          loading={false}
          disabled={false}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          startIcon={<SearchIcon isHovered={isHovered} />}
          placeholder='Procurar veículos, motoristas e etc...'
          onClick={() => {
            trackGlobalSearchEvents.openSearch();
            onOpen();
          }}
        />
        <kbd className='absolute right-2 inline-flex h-5 max-h-full items-center rounded border border-gray-5 bg-gray-2 px-1 font-[inherit] font-medium text-gray-a9 text-xs shadow-sm'>
          {isMac ? '⌘' : 'Ctrl'} + K
        </kbd>
      </div>
    </div>
  );
};

const GlobalSearchError = () => {
  return (
    <div className='flex items-center justify-center'>
      <Button variant='surface' size='sm' color='danger' icon={<Icons name='x' />} />
    </div>
  );
};

const GlobalSearchTrigger = () => {
  const { t } = useTranslation('global-search');
  const { currentLanguage } = useLanguage();
  const location = useLocation().pathname;
  const vehicleGroupIds = useGlobalFilterStore((state: any) => state.vehicleGroupIds);
  const { sidebar } = useDashboard();
  const globalSidebarDef = useGlobalSidebarDef(t);
  const [hasBeenOpened, setHasBeenOpened] = useState(false);

  const getCategory = useCallback(
    (categoryId: string) => {
      const group = globalSidebarDef.find((category) => category.categories.find((item) => item.id === categoryId));
      return group?.categories.find((item) => item.id === categoryId);
    },
    [globalSidebarDef]
  );
  const pagesCategory = useMemo(() => getCategory(EnumCategory.PAGES), [getCategory]);

  const pagesWithCategory = useMemo<PageWithCategory[]>(() => {
    if (!pagesCategory) return [];

    return sidebar.pagesFlatted
      .filter((page) => !page.sidebar?.hide)
      .filter((page) => page.url)
      .map((page) => {
        const pageTitle = getTextForLanguage(page.title, currentLanguage);
        const pageDescription = getTextForLanguage(page.description, currentLanguage);

        return {
          ...page,
          id: pageTitle?.toLowerCase().replace(/\s+/g, '-') || '',
          primary: page.primary || '',
          category: pagesCategory,
          description: pageDescription,
        };
      });
  }, [currentLanguage, pagesCategory, sidebar.pagesFlatted]);

  const { isLoading, fuse, items, isRefetching, error, refresh } = useSearchData<Category, PageWithCategory>({
    currentLocation: location,
    vehicleGroupIds,
    pagesData: pagesWithCategory,
    getCategory,
    enabled: hasBeenOpened,
  });

  useEffect(() => {
    if (hasBeenOpened && vehicleGroupIds.length > 0) refresh?.();
  }, [hasBeenOpened, refresh, vehicleGroupIds]);

  if (error) return <GlobalSearchError />;

  if (hasBeenOpened && isLoading) return <GlobalSearchLoading />;

  if (fuse && items) {
    return (
      <GlobalSearchProvider fuse={fuse} data={items as DataWithCategory[]} initialOpen={hasBeenOpened}>
        <GlobalSearchContent handleUpdateData={refresh} isRefetching={isRefetching} />
      </GlobalSearchProvider>
    );
  }

  return <GlobalSearchIdleInput onOpen={() => setHasBeenOpened(true)} />;
};

const GlobalSearch = () => {
  const { runtimeFlags } = useDashboard();
  const hasValidGroupOrVehicles = !(runtimeFlags.noValidGroupOrVehicles ?? false);
  if (!hasValidGroupOrVehicles) return null;

  return (
    <ErrorBoundary
      fallback={<GlobalSearchError />}
      onError={(error, info) => {
        DatadogTrack.error(error, { component: 'GlobalSearch', info });
      }}
    >
      <Suspense fallback={<GlobalSearchLoading />}>
        <ProviderPersistQueryClient>
          <GlobalSearchTrigger />
        </ProviderPersistQueryClient>
      </Suspense>
    </ErrorBoundary>
  );
};

export default GlobalSearch;
